import { Injectable, inject } from '@angular/core';

import { SelectOption } from '@gea/digital-ui-lib';
import { Observable, catchError, first, map, of } from 'rxjs';

import { HrtApiService } from '../hrt-api/hrt-api.service';
import { FaultAnalysisDto, MachineFaultDto, FetchFaultCodesParams, FetchFaultAnalysisParams, FaultStatus } from '@shared/models';
import { DateHelper } from '@brc/shared/helpers';
import { PredictiveAlert, PredictiveAlertTree } from '@shared/models';

const WEEK_DATA_AGGREGATION_THRESHOLD = 14;

@Injectable({
  providedIn: 'root',
})
export class FaultsApiService {
  readonly faultsUrl = (machineId: number) => `machines/${machineId}/faults`;
  readonly api = inject(HrtApiService);

  fetchFaultCodes({ machines, dateRange }: FetchFaultCodesParams): Observable<{
    faultCodes: SelectOption[];
    faultCodeGroups: SelectOption[];
  }> {
    const requestParams: FetchFaultCodesParams = {
      machines,
      // @ts-expect-error: Type 'string' is not assignable to type 'number'.ts(2322)
      dateRange: DateHelper.daysBack(dateRange ?? 28),
    };

    // Use lightweight faultsList endpoint instead of full faultanalysis
    type FaultListItem = { faultCode: number; faultCodeGroup: number; name?: string };
    return this.api.getForOrganization<FaultListItem[]>('faultsList', { params: { ...requestParams } }).pipe(
      map((list: FaultListItem[] | undefined) => {
        const faultCodesByValue = new Map<number, SelectOption>();
        const groupNamesById = new Map<number, string>();
        (list ?? []).forEach((item: FaultListItem) => {
          if (!faultCodesByValue.has(item.faultCode)) {
            faultCodesByValue.set(item.faultCode, { name: item.name ?? '', value: item.faultCode });
          }
          const groupId = Number(item.faultCodeGroup);
          if (!groupNamesById.has(groupId)) groupNamesById.set(groupId, String(item.name ?? ''));
        });
        const faultCodeGroups = Array.from(groupNamesById, ([value, name]) => ({ name, value }));

        return { faultCodes: Array.from(faultCodesByValue.values()), faultCodeGroups };
      }),
      catchError(() => of({ faultCodes: [], faultCodeGroups: [] })),
      first()
    );
  }

  getPredictiveAlert(machines: (string | number)[], dateRange?: number): Observable<(PredictiveAlert | PredictiveAlertTree)[]> {
    return this.api.getForOrganization<(PredictiveAlert | PredictiveAlertTree)[]>('predictivealert', {
      params: { machines, ...(dateRange !== undefined && { dateRange }) },
    });
  }

  fetchFaultAnalysis({
    machines,
    dateRange,
    faults = [],
    faultStatus = [],
  }: FetchFaultAnalysisParams): Observable<FaultAnalysisDto> {
    const convertedDateRange = DateHelper.daysBack(dateRange ?? 28);
    const aggregation = this.getDataAggregationType(convertedDateRange);
    const { normalizeCountPendingRelative } = this;

    return this.api
      .getForOrganization<FaultAnalysisDto>('faultanalysis', {
        params: {
          machines,
          dateRange: convertedDateRange,
          faults,
          faultStatus: faultStatus.map((status) => (status === FaultStatus.PENDING ? '0' : '1')),
          aggregation,
        },
      })
      .pipe(
        map((faultAnalysis) => {
          return {
            ...faultAnalysis,
            shutdowns: normalizeCountPendingRelative(faultAnalysis.shutdowns),
            warnings: normalizeCountPendingRelative(faultAnalysis.warnings),
          };
        })
      );
  }

  // Helper method to determine data aggregation type based on date range
  private getDataAggregationType(dateRange?: string): 'DAY' | 'WEEK' {
    return Number(dateRange) > WEEK_DATA_AGGREGATION_THRESHOLD ? 'WEEK' : 'DAY';
  }

  // Normalize the relative count of pending issues in fault data
  private normalizeCountPendingRelative<T extends MachineFaultDto>(faults: T[]): T[] {
    const totalPending = faults.reduce((total, fault) => total + fault.countPending, 0);
    return faults.map((fault) => ({ ...fault, countPendingRelative: totalPending ? fault.countPending / totalPending : 0 }));
  }
}
